import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosConfig';

export function usePreInvoices(initialSize = 20) {
    const [invoices, setInvoices] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(initialSize);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [sorting, setSorting] = useState({ key: null, direction: null });
    const [prevSorting, setPrevSorting] = useState({ key: null, direction: null });
    const [isLoading, setIsLoading] = useState(false);

    const fetchInvoices = (pageToFetch, sort, reset = false) => {
        setIsLoading(true);
        axiosInstance.get(`/api/invoices/pre-invoice?page=${pageToFetch}&size=${size}&sortProperty=${sort.key}&sortDirection=${sort.direction}`)
            .then(response => response.data)
            .then(data => {
                const items = data.content !== undefined ? data.content : data;
                const total = data.totalElements !== undefined ? data.totalElements : items.length;
                const pages = data.totalPages !== undefined ? data.totalPages : 1;
                
                setInvoices(items);
                setTotalElements(total);
                setTotalPages(pages);
            })
            .catch(error => console.error('Error fetching pre-invoices:', error))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (prevSorting.key !== sorting.key || prevSorting.direction !== sorting.direction) {
            setInvoices([]);
            setPage(0);
            fetchInvoices(0, sorting, true);
            setPrevSorting(sorting);
        } else {
            fetchInvoices(page, sorting, false);
        }
    }, [page, size, sorting]);

    const createInvoice = async (formData) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('jwtToken');
            const response = await fetch('/api/invoices/pre-invoice?save=true', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            window.URL.revokeObjectURL(url);
            
            setInvoices([]);
            setPage(0);
            fetchInvoices(0, sorting, true);
        } catch (error) {
            console.error('Error creating pre-invoice:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateInvoice = async (invoice) => {
        setIsLoading(true);
        try {
            const payload = {
                ...invoice,
                issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
            };
            await axiosInstance.put(`/api/invoices/pre-invoice/${invoice.id}`, payload);
            setInvoices([]);
            setPage(0);
            fetchInvoices(0, sorting, true);
        } catch (error) {
            console.error('Error updating pre-invoice:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteInvoice = async (invoice) => {
        setIsLoading(true);
        try {
            await axiosInstance.delete(`/api/invoices/pre-invoice/${invoice.id}`);
            setInvoices(prevInvoices => prevInvoices.filter(inv => inv.id !== invoice.id));
        } catch (error) {
            console.error('Error deleting pre-invoice:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const generatePdf = async (e, invoice) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                ...invoice,
                issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
            };
            const token = localStorage.getItem('jwtToken');
            const response = await fetch('/api/invoices/pre-invoice?save=false', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadPdf = async (e, invoice) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                ...invoice,
                issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
            };
            const token = localStorage.getItem('jwtToken');
            const response = await fetch('/api/invoices/pre-invoice?save=false', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `pre_invoice_${invoice.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        invoices,
        sorting,
        setSorting,
        page,
        setPage,
        size,
        setSize,
        totalPages,
        totalElements,
        createInvoice,
        updateInvoice,
        deleteInvoice,
        generatePdf,
        downloadPdf
    };
}
